import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';

import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import IconButton from '@material-ui/core/IconButton';

import CloseIcon from '@material-ui/icons/Close';

import Loading from 'app/components/loaders/Loading';

import useApp from 'app/context/useApp';
import useApi from 'app/context/useApi';

const useStyles = makeStyles(theme => ({
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    },
    marginBottomSmall: {
        marginBottom: theme.spacing(1)
    },
}));

const initNotification = {
    subject: "",
    message: "",
    action_text: "",
    action_url: "",
    send_in_app: true,
    send_emails: false,
    send_sms: false,
    post_as: "club",
    group_id: 0,
    include_children: true
}

export default function SendNotificationDialog(props) {

    const { app } = useApp();
    const classes = useStyles();
    const api = useApi();

    const [ notification, setNotification ] = useState(initNotification);
    const [ groups, setGroups ] = useState([]);

    const [ loading, setLoading ] = useState(false);
    const [ errors, setErrors ] = useState({});

    const { open, onClose, onSave, onClosed } = props;

    // Getter functions ------------------------------------------

    const getGroups = () => {
        setLoading(true);

        api.get('/admin/group')
            .then(res => setGroups(res.groups))
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        getGroups();
    }, []);

    // Setter Functions ------------------------------------------

    const togglePostingAs = () => {
        const val = (notification.post_as == 'club') ? 'user' : 'club';
        setNotification({...notification, post_as: val})
    }

    const handleChange = name => event => {
        const target = event.target;
        const value = (target.type === 'checkbox' ? target.checked : target.value);

        if (isError(name)) delete errors[name];

        setNotification({ ...notification, [name]: value });
    };

    // Post functions ------------------------------------------

    const postNotification = () => {
        setLoading(true);

        api.post(`/admin/notification`, notification)
            .then(() => onSave())
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            }).finally(() => setLoading(false));
    }

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    const onExited = () => {
        setNotification(initNotification);
        if (onClosed) onClosed();
    }

    // Constants ----------------------------------------

    let postingAs = (notification.post_as == 'club') ? app.club.name : app.user.first + " " + app.user.last;

    const membersGroup = useMemo(() => {
        if (groups.length) {
            return groups.find(group => group.membership_level == true && group.members_level == true && group.parent_id === 0);
        }
        return {id: 0}
    }, [groups]);

    useEffect(() => {
        if (notification.group_id === 0) {
            setNotification({...notification, group_id: membersGroup.id});
        }
    }, [membersGroup]);

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="sm"
            onClose={onClose}
            onExited={onExited}
            aria-labelledby="send-notification-dialog-title"
            >
            <Loading isLoading={loading}/>
            <DialogTitle id="send-notification-dialog-title">
                <span>Send a Notification</span>
                <IconButton aria-label="Cancel" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <form noValidate autoComplete="off">
                    <Grid
                        container
                        spacing={1}
                        alignItems="center"
                        className={classes.marginBottomSmall}
                        >
                        <Grid item>
                            <Typography variant="subtitle2">Sending As:</Typography>
                        </Grid>
                        <Grid item>
                            <Button
                                color="primary"
                                onClick={togglePostingAs}
                                >
                                {postingAs}
                            </Button>
                        </Grid>
                    </Grid>
                    <TextField
                        label="Subject"
                        required
                        value={notification.subject}
                        onChange={handleChange('subject')}
                        fullWidth
                        error={isError('subject')}
                        helperText={getErrorMessage('subject')}
                        variant="outlined"
                        className={classes.marginBottom}
                    />
                    <TextField
                        label="Message"
                        multiline
                        rows="4"
                        value={notification.message}
                        onChange={handleChange('message')}
                        fullWidth
                        error={isError('message')}
                        helperText={getErrorMessage('message')}
                        required
                        variant="outlined"
                        className={classes.marginBottom}
                    />
                    <TextField
                        label="Action Text"
                        value={notification.action_text}
                        onChange={handleChange('action_text')}
                        fullWidth
                        error={isError('action_text')}
                        helperText={getErrorMessage('action_text')}
                        variant="outlined"
                        className={classes.marginBottom}
                    />
                    <TextField
                        label="Action Url"
                        value={notification.action_url}
                        onChange={handleChange('action_url')}
                        fullWidth
                        error={isError('action_url')}
                        helperText={getErrorMessage('action_url')}
                        variant="outlined"
                        className={classes.marginBottom}
                    />
                    <Typography gutterBottom variant="subtitle2">Delivery Methods:</Typography>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={notification.send_in_app}
                                onChange={handleChange('send_in_app')}
                                name="send_in_app"
                                color="primary"
                            />
                        }
                        label="Notify Users in App"
                    /><br/>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={notification.send_emails}
                                onChange={handleChange('send_emails')}
                                name="send_emails"
                                color="primary"
                            />
                        }
                        label="Send Emails to Users"
                    /><br/>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={notification.send_sms}
                                onChange={handleChange('send_sms')}
                                name="send_sms"
                                color="primary"
                                disabled
                            />
                        }
                        label="Send Text Messages to Users"
                        className={classes.marginBottom}
                    />
                    <TextField
                        id="group-select"
                        select
                        label="Select Notification Group"
                        value={notification.group_id}
                        onChange={handleChange('group_id')}
                        helperText={getErrorMessage('group_id')}
                        variant="outlined"
                        fullWidth
                        className={classes.marginBottomSmall}
                        >
                        <MenuItem key={0} value={0}>
                            Entire Club
                        </MenuItem>
                        {groups.map(group => (
                            <MenuItem key={group.id} value={group.id}>
                                {group.title}
                            </MenuItem>
                        ))}
                    </TextField>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={notification.include_children}
                                onChange={handleChange('include_children')}
                                name="include_children"
                                color="primary"
                            />
                        }
                        label="Send to Child Groups"
                        className={classes.marginBottom}
                    />
                </form>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={postNotification}
                    color="primary"
                    variant="contained"
                    fullWidth
                    disabled={loading}
                    >
                    Send Notification
                </Button>
            </DialogActions>
        </Dialog>
    );
}
